import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '../ui/card';
import { toast } from 'sonner';
import { Package, Receipt, Warehouse, Wallet, TrendingUp, ChevronDown, ChevronRight, Banknote } from 'lucide-react';
import { formatCurrency, formatMonth } from './utils';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/api\/?$/, '');
const API = `${BACKEND_URL}/api`;

export function AccountBalanceReport() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedSections, setExpandedSections] = useState({
    materialPurchased: false,
    expenses: false
  });

  useEffect(() => {
    fetchAccountBalance();
  }, []);

  const fetchAccountBalance = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/profit-report`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(response.data?.account_balance);
    } catch (error) {
      toast.error('Failed to load account balance');
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="w-6 h-6 border-2 border-slate-300 border-t-amber-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (!data) {
    return (
      <Card className="p-12 text-center border-0 rounded-2xl shadow-lg bg-white">
        <Wallet className="w-12 h-12 text-slate-300 mx-auto mb-4" />
        <p className="text-slate-600">No data available</p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-bold text-slate-900">Account Balance</h3>
        <p className="text-sm text-slate-500">Financial summary and inventory overview</p>
      </div>

      <div className="space-y-4">
        {/* 1. Opening Capital - Static */}
        <Card className="border-0 rounded-2xl shadow-lg bg-gradient-to-br from-emerald-500 to-emerald-600 p-4 sm:p-5 overflow-hidden">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <Banknote className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs sm:text-sm text-white/80 font-medium">Opening Capital</p>
              <p className="text-lg sm:text-xl md:text-2xl font-bold text-white truncate" style={{ fontFamily: "'Manrope', sans-serif" }}>
                {formatCurrency(data.opening_capital || 0)}
              </p>
              <p className="text-xs text-white/70 mt-1 truncate">Total cash injected from Investment</p>
            </div>
          </div>
        </Card>

        {/* 2. Material Purchased - Expandable */}
        <Card className="border-0 rounded-2xl shadow-lg bg-white overflow-hidden">
          <button 
            onClick={() => toggleSection('materialPurchased')}
            className="w-full p-4 sm:p-5 flex items-center justify-between hover:bg-slate-50 transition-colors"
          >
            <div className="flex items-center gap-3 sm:gap-4 min-w-0 flex-1">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl flex items-center justify-center shadow-lg shadow-amber-500/30 flex-shrink-0">
                <Package className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <div className="text-left min-w-0 flex-1">
                <p className="text-xs sm:text-sm text-slate-500 font-medium">Material Purchased</p>
                <p className="text-lg sm:text-xl md:text-2xl font-bold text-slate-900 truncate" style={{ fontFamily: "'Manrope', sans-serif" }}>
                  {formatCurrency(data.material_purchased?.total || 0)}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1 sm:gap-2 text-slate-400 flex-shrink-0 ml-2">
              <span className="text-xs sm:text-sm hidden sm:inline">Monthly breakdown</span>
              {expandedSections.materialPurchased ? (
                <ChevronDown className="w-5 h-5" />
              ) : (
                <ChevronRight className="w-5 h-5" />
              )}
            </div>
          </button>
          
          {expandedSections.materialPurchased && (
            <div className="border-t bg-amber-50 p-4">
              <h4 className="text-sm font-semibold text-amber-800 mb-3">Monthly Material Purchases</h4>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {data.material_purchased?.monthly?.length > 0 ? (
                  data.material_purchased.monthly.map((item, idx) => (
                    <div key={idx} className="flex justify-between py-2 px-3 bg-white rounded-lg">
                      <span className="text-slate-600">{formatMonth(item.month)}</span>
                      <span className="font-semibold text-amber-700">{formatCurrency(item.amount)}</span>
                    </div>
                  ))
                ) : (
                  <p className="text-slate-500 text-sm">No purchases recorded</p>
                )}
              </div>
            </div>
          )}
        </Card>

        {/* 3. Expenses - Expandable */}
        <Card className="border-0 rounded-2xl shadow-lg bg-white overflow-hidden">
          <button 
            onClick={() => toggleSection('expenses')}
            className="w-full p-4 sm:p-5 flex items-center justify-between hover:bg-slate-50 transition-colors"
          >
            <div className="flex items-center gap-3 sm:gap-4 min-w-0 flex-1">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-red-500 to-red-600 rounded-xl flex items-center justify-center shadow-lg shadow-red-500/30 flex-shrink-0">
                <Receipt className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <div className="text-left min-w-0 flex-1">
                <p className="text-xs sm:text-sm text-slate-500 font-medium">Expenses (YTD)</p>
                <p className="text-lg sm:text-xl md:text-2xl font-bold text-slate-900 truncate" style={{ fontFamily: "'Manrope', sans-serif" }}>
                  {formatCurrency(data.expenses?.total || 0)}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1 sm:gap-2 text-slate-400 flex-shrink-0 ml-2">
              <span className="text-xs sm:text-sm hidden sm:inline">Category breakdown</span>
              {expandedSections.expenses ? (
                <ChevronDown className="w-5 h-5" />
              ) : (
                <ChevronRight className="w-5 h-5" />
              )}
            </div>
          </button>
          
          {expandedSections.expenses && (
            <div className="border-t bg-red-50 p-4">
              <h4 className="text-sm font-semibold text-red-800 mb-3">Category-wise Expenses</h4>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {data.expenses?.by_category?.length > 0 ? (
                  data.expenses.by_category.map((item, idx) => (
                    <div key={idx} className="flex justify-between py-2 px-3 bg-white rounded-lg">
                      <span className="text-slate-600">{item.category}</span>
                      <span className="font-semibold text-red-700">{formatCurrency(item.amount)}</span>
                    </div>
                  ))
                ) : (
                  <p className="text-slate-500 text-sm">No expenses recorded</p>
                )}
              </div>
            </div>
          )}
        </Card>

        {/* 4. Inventory - Static */}
        <Card className="border-0 rounded-2xl shadow-lg bg-gradient-to-br from-purple-500 to-purple-600 p-4 sm:p-5 overflow-hidden">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <Warehouse className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs sm:text-sm text-white/80 font-medium">Inventory (Unsent Silica Value)</p>
              <p className="text-lg sm:text-xl md:text-2xl font-bold text-white truncate" style={{ fontFamily: "'Manrope', sans-serif" }}>
                {formatCurrency(data.inventory?.value || 0)}
              </p>
              <p className="text-xs sm:text-sm text-white/70 mt-1 truncate">
                {(data.inventory?.tons || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })} tons remaining
              </p>
            </div>
          </div>
        </Card>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* 5. Net Balance */}
          <Card className="border-0 rounded-2xl shadow-lg bg-white p-4 sm:p-5 overflow-hidden">
            <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Wallet className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600" />
              </div>
              <p className="text-xs sm:text-sm text-slate-500 font-medium truncate">Net Balance</p>
            </div>
            <p className={`text-lg sm:text-xl md:text-2xl font-bold truncate ${(data.net_balance || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`} style={{ fontFamily: "'Manrope', sans-serif" }}>
              {formatCurrency(data.net_balance || 0)}
            </p>
            <p className="text-xs text-slate-400 mt-2 truncate">Opening Capital + Accepted Paid + Other Income − Expenses</p>
            <div className="mt-2 pt-2 border-t text-xs text-slate-500 flex flex-wrap gap-1" data-testid="net-balance-breakdown">
              <span className="text-emerald-600" title="Opening Capital">{formatCurrency(data.opening_capital || 0)}</span>
              <span className="text-slate-400">+</span>
              <span className="text-blue-600" title="Accepted Paid Loads">{formatCurrency(data.total_paid_accepted_loads || 0)}</span>
              <span className="text-slate-400">+</span>
              <span className="text-teal-600" title="Other Income (Credits)">{formatCurrency(data.total_other_income || 0)}</span>
              <span className="text-slate-400">−</span>
              <span className="text-red-600" title="Expenses">{formatCurrency(data.expenses?.total || 0)}</span>
            </div>
          </Card>

          {/* 6. Net Balance + Accounts Receivable */}
          <Card className="border-0 rounded-2xl shadow-lg bg-white p-4 sm:p-5 overflow-hidden">
            <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
              </div>
              <p className="text-xs sm:text-sm text-slate-500 font-medium truncate">Net Balance + A/R</p>
            </div>
            <p className={`text-lg sm:text-xl md:text-2xl font-bold truncate ${(data.net_balance_plus_receivable || 0) >= 0 ? 'text-blue-600' : 'text-red-600'}`} style={{ fontFamily: "'Manrope', sans-serif" }}>
              {formatCurrency(data.net_balance_plus_receivable || 0)}
            </p>
            <p className="text-xs text-slate-400 mt-2 truncate">Net Balance + Pending Loads</p>
            <div className="mt-2 pt-2 border-t text-xs text-slate-500 flex flex-wrap gap-1">
              <span className={`${(data.net_balance || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{formatCurrency(data.net_balance || 0)}</span>
              <span>+</span>
              <span className="text-amber-600">{formatCurrency(data.accounts_receivable || 0)}</span>
            </div>
          </Card>

          {/* 7. Net Balance + A/R + Inventory */}
          <Card className="border-0 rounded-2xl shadow-lg bg-gradient-to-br from-slate-800 to-slate-900 p-4 sm:p-5 overflow-hidden">
            <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <p className="text-xs sm:text-sm text-white/80 font-medium truncate">Net + A/R + Inventory</p>
            </div>
            <p className="text-lg sm:text-xl md:text-2xl font-bold text-white truncate" style={{ fontFamily: "'Manrope', sans-serif" }}>
              {formatCurrency(data.net_balance_plus_receivable_plus_inventory || 0)}
            </p>
            <p className="text-xs text-white/60 mt-2 truncate">Net Balance + Pending + Inventory</p>
            <div className="mt-2 pt-2 border-t border-white/20 text-xs text-white/70 flex flex-wrap gap-1">
              <span className={`${(data.net_balance || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{formatCurrency(data.net_balance || 0)}</span>
              <span>+</span>
              <span className="text-amber-400">{formatCurrency(data.accounts_receivable || 0)}</span>
              <span>+</span>
              <span className="text-purple-400">{formatCurrency(data.inventory?.value || 0)}</span>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
